import React, { useState, useEffect, useMemo } from 'react'
import { Button, Tree, Input, Modal, Form, Select, InputNumber, message, Space, Tag, Tooltip, Spin } from 'antd'
import { ApiRequest } from '../../../../services/api/api'
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, FolderOutlined, FileOutlined } from '@ant-design/icons'
import { useDocumentTitle } from '../../../../services/title/useDocumentTitle'
import SweetAlertService from '../../../../services/lib/utils/sweet-alert-service'
import { getUserId } from '../../../../services/lib/utils/auth.utils'

const { Search } = Input
const { Option } = Select

interface TreeNode {
  title: React.ReactNode
  key: string
  children?: TreeNode[]
  classificationId: number
  parentId: number | null
  level: number
  sortOrder: number
  status: number
  notes: string
  name: string
  parentName: string | null
}

interface ParentOption {
  classificationId: number
  name: string
  level: number
}

export const ProductClassificationManagementPage = () => {
  useDocumentTitle()

  const [treeData, setTreeData] = useState<TreeNode[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [autoExpandParent, setAutoExpandParent] = useState(true)

  const [modalVisible, setModalVisible] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [currentNode, setCurrentNode] = useState<TreeNode | null>(null)
  const [form] = Form.useForm()

  // 获取分类树数据
  const fetchClassificationTree = async () => {
    setLoading(true)
    try {
      const response = await ApiRequest({
        url: 'ProductClassification/GetTree',
        method: 'get',
        isShowSpinner: true
      })

      if (response.data && response.data.data) {
        setTreeData(transformToTreeData(response.data.data))
      } else {
        // 后端没有现成的树接口时，用 GetAll 拉扁平数据自行组装
        const fallbackResponse = await ApiRequest({
          url: 'ProductClassification/GetAll?includeDeleted=false',
          method: 'get',
          isShowSpinner: false
        })

        if (fallbackResponse.data && fallbackResponse.data.data) {
          setTreeData(buildTreeFromFlatData(fallbackResponse.data.data))
        }
      }
    } catch (error) {
      console.error('Failed to fetch classification tree:', error)
      message.error('Failed to load product classifications')
    } finally {
      setLoading(false)
    }
  }

  // 备用方法：从扁平数据构建树（GetAll 接口场景）
  const buildTreeFromFlatData = (flatData: any[]): TreeNode[] => {
    const dataMap = new Map<number, TreeNode>()
    const rootNodes: TreeNode[] = []

    flatData.forEach(item => {
      dataMap.set(item.classificationId, {
        title: item.name,
        classificationId: item.classificationId,
        name: item.name,
        key: item.classificationId.toString(),
        parentId: item.parentId,
        level: item.level,
        sortOrder: item.sortOrder,
        status: item.status,
        notes: item.notes || '',
        parentName: item.parentName || null,
        children: []
      })
    })

    flatData.forEach(item => {
      const node = dataMap.get(item.classificationId)
      if (!node) return

      if (item.parentId && dataMap.has(item.parentId)) {
        dataMap.get(item.parentId)!.children!.push(node)
      } else {
        rootNodes.push(node)
      }
    })

    const cleanEmptyChildren = (nodes: TreeNode[]) => {
      nodes.forEach(node => {
        if (node.children && node.children.length === 0) {
          delete node.children
        } else if (node.children) {
          cleanEmptyChildren(node.children)
        }
      })
    }
    cleanEmptyChildren(rootNodes)

    return rootNodes
  }

  // 转换 GetTree 接口返回的嵌套数据为 Tree 组件所需格式
  const transformToTreeData = (data: any[]): TreeNode[] => {
    return data.map(item => ({
      title: item.name,
      key: item.classificationId.toString(),
      classificationId: item.classificationId,
      parentId: item.parentId,
      level: item.level,
      sortOrder: item.sortOrder,
      status: item.status,
      notes: item.notes || '',
      name: item.name,
      parentName: item.parentName || null,
      children: item.children && item.children.length > 0
        ? transformToTreeData(item.children)
        : undefined
    }))
  }

  // 获取父级分类选项（用于添加/编辑时选择）
  const getParentOptions = (): ParentOption[] => {
    const flattenTree = (nodes: TreeNode[]): ParentOption[] => {
      let result: ParentOption[] = []
      nodes.forEach(node => {
        result.push({ classificationId: node.classificationId, name: node.name, level: node.level })
        if (node.children) {
          result = result.concat(flattenTree(node.children))
        }
      })
      return result
    }

    let flatData = flattenTree(treeData)

    // 添加子分类模式下，排除第三级分类作为父选项，防止创建第四级
    if (modalType === 'add' && currentNode) {
      flatData = flatData.filter(item => item.level < 3)
    }

    // 编辑模式下排除当前节点及其所有子孙节点，避免父级选到自己形成循环引用
    if (modalType === 'edit' && currentNode) {
      const getDescendantIds = (node: TreeNode): number[] => {
        let ids = [node.classificationId]
        node.children?.forEach(child => {
          ids = ids.concat(getDescendantIds(child))
        })
        return ids
      }
      const excludeIds = getDescendantIds(currentNode)
      flatData = flatData.filter(item => !excludeIds.includes(item.classificationId))
    }

    return flatData
  }

  useEffect(() => {
    fetchClassificationTree()
  }, [])

  const onExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue)
    setAutoExpandParent(false)
  }

  const onSelect = (selectedKeysValue: React.Key[]) => {
    setSelectedKeys(selectedKeysValue)
  }

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSearchValue(value)
    setExpandedKeys(getParentKeys(value, treeData))
    setSelectedKeys([])
    setAutoExpandParent(true)
  }

  // 获取匹配节点的父级键，保证搜索命中的子节点在树里可见
  const getParentKeys = (value: string, tree: TreeNode[]): React.Key[] => {
    if (!value.trim()) return []
    const expandKeys: React.Key[] = []

    const searchInTree = (nodes: TreeNode[], parentKeys: React.Key[] = []) => {
      nodes.forEach(node => {
        if (node.name.toLowerCase().includes(value.toLowerCase())) {
          expandKeys.push(...parentKeys)
        }
        if (node.children) {
          searchInTree(node.children, [...parentKeys, node.key])
        }
      })
    }

    searchInTree(tree)
    return Array.from(new Set(expandKeys))
  }

  // 高亮搜索命中的子串
  const highlightMatch = (name: string) => {
    if (!searchValue.trim()) return <span>{name}</span>
    const idx = name.toLowerCase().indexOf(searchValue.toLowerCase())
    if (idx === -1) return <span>{name}</span>
    return (
      <span>
        {name.substring(0, idx)}
        <span style={{ backgroundColor: '#ffe58f' }}>{name.substring(idx, idx + searchValue.length)}</span>
        {name.substring(idx + searchValue.length)}
      </span>
    )
  }

  const onAddRoot = () => {
    setModalType('add')
    setCurrentNode(null)
    form.resetFields()
    form.setFieldsValue({ parentId: null, sortOrder: getNextSortOrder(null) })
    setModalVisible(true)
  }

  const onAddChild = (node: TreeNode) => {
    if (node.level >= 3) {
      message.warning('Maximum 3 classification levels allowed')
      return
    }
    setModalType('add')
    setCurrentNode(node)
    form.resetFields()
    form.setFieldsValue({ parentId: node.classificationId, sortOrder: getNextSortOrder(node.classificationId) })
    setModalVisible(true)
  }

  const onEdit = (node: TreeNode) => {
    setModalType('edit')
    setCurrentNode(node)
    form.resetFields()
    form.setFieldsValue({
      classificationId: node.classificationId,
      name: node.name,
      parentId: node.parentId,
      sortOrder: node.sortOrder,
      notes: node.notes
    })
    setModalVisible(true)
  }

  const onDelete = async (node: TreeNode) => {
    if (node.children && node.children.length > 0) {
      message.warning('Please delete child classifications first')
      return
    }

    try {
      const result = await SweetAlertService.confirmMessage(
        `Are you sure you want to delete the classification "<b>${node.name}</b>"?`
      )

      if (result) {
        await ApiRequest({
          url: `ProductClassification/Delete/${node.classificationId}`,
          method: 'delete',
          isShowSpinner: true
        })

        message.success('Classification deleted successfully')
        await fetchClassificationTree()
      }
    } catch (error: any) {
      console.error('Failed to delete classification:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete classification'
      message.error(errorMessage)
    }
  }

  // Status toggle function - directly toggle status without entering Modal
  const onToggleStatus = async (node: TreeNode) => {
    const nextStatus = node.status === 1 ? 0 : 1
    try {
      await ApiRequest({
        url: 'ProductClassification/Update',
        method: 'put',
        data: {
          classificationId: node.classificationId,
          name: node.name,
          parentId: node.parentId,
          sortOrder: node.sortOrder,
          notes: node.notes,
          status: nextStatus,
          updatedBy: getUserId()
        },
        isShowSpinner: false
      })
      message.success(nextStatus === 1 ? 'Classification activated' : 'Classification deactivated')
      await fetchClassificationTree()
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update status'
      message.error(errorMessage)
    }
  }

  const getNextSortOrder = (parentId: number | null): number => {
    if (!parentId) {
      const rootNodes = treeData.filter(node => !node.parentId)
      return rootNodes.length > 0 ? Math.max(...rootNodes.map(node => node.sortOrder)) + 1 : 1
    }
    const parent = findNodeById(treeData, parentId)
    if (parent && parent.children && parent.children.length > 0) {
      return Math.max(...parent.children.map(node => node.sortOrder)) + 1
    }
    return 1
  }

  const findNodeById = (nodes: TreeNode[], id: number): TreeNode | null => {
    for (const node of nodes) {
      if (node.classificationId === id) return node
      if (node.children) {
        const found = findNodeById(node.children, id)
        if (found) return found
      }
    }
    return null
  }

  const onSubmit = async (values: any) => {
    try {
      if (modalType === 'add') {
        // Check level limit when adding child classification
        if (currentNode && currentNode.level >= 3) {
          message.error('Maximum 3 classification levels allowed')
          return
        }

        // When adding, set default status to 1 (Active)
        await ApiRequest({
          url: 'ProductClassification/Add',
          method: 'post',
          data: { ...values, status: 1, createdBy: getUserId() },
          isShowSpinner: true
        })
        message.success('Classification added successfully')
      } else {
        // When editing, preserve the original status from current node
        const updateData = {
          ...values,
          status: currentNode?.status || 1,
          updatedBy: getUserId()
        }
        await ApiRequest({
          url: 'ProductClassification/Update',
          method: 'put',
          data: updateData,
          isShowSpinner: true
        })
        message.success('Classification updated successfully')
      }

      setModalVisible(false)
      await fetchClassificationTree()
    } catch (error: any) {
      console.error('Failed to save classification:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save classification'
      message.error(errorMessage)
    }
  }

  const renderTreeNodes = (nodes: TreeNode[]): any[] => {
    return nodes.map(node => {
      const hasChildren = !!node.children && node.children.length > 0
      return {
        ...node,
        title: (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr auto',
              gap: '16px',
              alignItems: 'center',
              width: '100%',
              padding: '10px 16px',
              borderBottom: '1px solid #f0f0f0',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#fafafa'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            {/* Classification Name - with icon and text */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              {hasChildren ? <FolderOutlined style={{ color: '#1890ff', fontSize: '16px' }} /> : <FileOutlined style={{ color: '#999', fontSize: '16px' }} />}
              <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {highlightMatch(node.name)}
              </span>
            </div>

            {/* Sort Order */}
            <div style={{ textAlign: 'center' }}>
              <Tooltip title={`Sort Order: ${node.sortOrder}`}>
                <div style={{
                  background: '#f5f5f5',
                  color: '#8c8c8c',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '2px 8px',
                  borderRadius: '8px',
                  display: 'inline-block',
                  border: '1px solid #e8e8e8'
                }}>
                  {node.sortOrder}
                </div>
              </Tooltip>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Tooltip title={node.level >= 3 ? 'Maximum 3 levels allowed' : 'Add Child'}>
                <Button
                  type="default"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddChild(node)
                  }}
                  disabled={node.level >= 3}
                  style={{
                    color: '#52c41a',
                    borderColor: '#52c41a',
                    width: '36px',
                    height: '32px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Tooltip>
              <Tooltip title="Edit">
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(node)
                  }}
                  style={{
                    color: '#1890ff',
                    borderColor: '#1890ff',
                    width: '36px',
                    height: '32px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Tooltip>
              <Tooltip title={hasChildren ? 'Please delete child classifications first' : 'Delete'}>
                <Button
                  type="default"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={hasChildren}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(node)
                  }}
                  style={{
                    width: '36px',
                    height: '32px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Tooltip>
            </div>
          </div>
        ),
        // 递归处理子节点，确保每个子节点都有操作按钮
        children: hasChildren ? renderTreeNodes(node.children!) : undefined
      }
    })
  }

  const renderedTree = useMemo(() => renderTreeNodes(treeData), [treeData, searchValue])

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Product Classification Management</h2>
        <Space size="middle">
          <Search placeholder="Search classifications..." allowClear onChange={onChangeSearch} style={{ width: 240 }} />
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchClassificationTree}
          >
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAddRoot}>
            Add Root Classification
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {treeData.length > 0 ? (
          <>
            {/* Table Header */}
            <div style={{
              background: '#fafafa',
              border: '1px solid #d9d9d9',
              borderBottom: 'none',
              borderRadius: '4px 4px 0 0',
              padding: '12px 16px',
              display: 'grid',
              gridTemplateColumns: '2fr 1fr auto',
              gap: '16px',
              fontWeight: 600,
              color: '#000000d9',
              fontSize: '14px',
              alignItems: 'center'
            }}>
              <div>Classification Name</div>
              <div style={{ textAlign: 'center' }}>Sort Order</div>
              <div style={{ textAlign: 'right' }}>Actions</div>
            </div>

            <Tree
              expandedKeys={expandedKeys}
              selectedKeys={selectedKeys}
              autoExpandParent={autoExpandParent}
              onExpand={onExpand}
              onSelect={onSelect}
              treeData={renderedTree}
              style={{
                background: '#fff',
                border: '1px solid #d9d9d9',
                borderRadius: '0 0 4px 4px',
                padding: '0'
              }}
              showLine={{ showLeafIcon: false }}
            />
          </>
        ) : !loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            No product classifications found. Click "Add Root Classification" to create one.
          </div>
        ) : null}
      </Spin>

      <Modal
        title={modalType === 'add' ? 'Add Classification' : 'Edit Classification'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item name="classificationId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label="Classification Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter classification name' },
              { max: 100, message: 'Maximum 100 characters' }
            ]}
          >
            <Input placeholder="Enter classification name" />
          </Form.Item>

          <Form.Item
            label="Parent Classification"
            name="parentId"
            tooltip={modalType === 'edit' ? 'Cannot modify parent when editing classification' : (currentNode === null ? 'Root classification has no parent' : '')}
          >
            <Select
              placeholder="Select parent classification (optional)"
              allowClear
              disabled={modalType === 'edit' || currentNode === null}
            >
              {getParentOptions().map(option => (
                <Option key={option.classificationId} value={option.classificationId}>
                  {'\u00A0\u00A0'.repeat(option.level - 1)}{option.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Sort Order"
            name="sortOrder"
            tooltip="Lower values appear first, compared within the same level"
            rules={[{ required: true, message: 'Please enter sort order' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Enter sort order" />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} placeholder="Enter notes (optional)" maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ProductClassificationManagementPage